/**
 * lib/experiences/titleDisplay.ts
 * ─────────────────────────────────────────────────────────────────────────────
 * Frontend-only display cleanup for experience titles. Never mutates backend
 * data, never touches booking_url or any field used for booking — this only
 * changes what's rendered on a card.
 *
 * Scope is deliberately mechanical, not editorial: shouting caps, doubled
 * punctuation, marketing exclamation marks, and immediately-adjacent
 * stutter-duplicates ("guiada guiada") are safe to clean up on ANY
 * provider's title without knowing what it means. A full rewrite
 * (translating, restructuring into "City: subtitle") would require
 * understanding each title's meaning, which this function deliberately does
 * not attempt — that risks corrupting titles we've never seen, across all
 * 30+ destinations, for the sake of a handful of known-ugly ones.
 * Local/reference titles are hand-authored editorial copy, so only the
 * always-safe typo-level fixes apply to them.
 *
 * Provider-Only Marketplace & Comparison V2 Foundation, Part C — REMOVED two
 * transforms that used to run here (any provider still using
 * TitleDisplayOptions.cityHint no longer has that field's second use):
 *   - collapseDuplicateCityMentions: dropped a *second* mention of the
 *     destination name anywhere in the title, assuming it was always a
 *     template artifact. It wasn't: "Palacio Real de Madrid con Entrada"
 *     lost its second "Madrid" to become "Palacio Real de con Entrada" —
 *     broken grammar, a real place name gutted.
 *   - collapseNonAdjacentDuplicateWords: dropped any later exact repeat of a
 *     5+ letter word anywhere in the title. For a two-word proper noun like
 *     "Mexico City"/"Panama City"/"Quebec City"/"New York [City]" repeated
 *     later in the same title, this could silently strip just ONE of the
 *     two words, leaving a dangling half-name ("...Tour of City" instead of
 *     "...Tour of Mexico City").
 * Both were removal-only transforms with no way to tell "template artifact"
 * apart from "a real word/name that happens to repeat" — see the regression
 * tests in titleDisplay.test.ts for the exact titles that broke. A provider
 * title may now read slightly repetitive; it is never grammatically broken.
 */

const SPANISH_LOWERCASE_CONNECTORS = new Set([
  "de", "del", "la", "las", "el", "los", "en", "y", "e", "a", "con", "por",
  "para", "al", "un", "una", "of", "in", "the", "and", "to", "with",
]);

function stripAccents(s: string): string {
  return s.normalize("NFD").replace(/[̀-ͯ]/g, "");
}

/** Titles that are mostly uppercase letters read as shouting, not emphasis. */
function isShouting(title: string): boolean {
  const letters = title.replace(/[^a-zA-ZÀ-ÿ]/g, "");
  if (letters.length < 8) return false;
  const upper = letters.replace(/[^A-ZÀ-Ý]/g, "");
  return upper.length / letters.length >= 0.7;
}

function toTitleCase(title: string): string {
  return title
    .toLowerCase()
    .split(" ")
    .map((word, idx) => {
      if (word.length === 0) return word;
      if (idx > 0 && SPANISH_LOWERCASE_CONNECTORS.has(word)) return word;
      return word.charAt(0).toUpperCase() + word.slice(1);
    })
    .join(" ");
}

/** Stutter-typo guard: "la la ciudad" → "la ciudad". Safe for any title. */
function collapseAdjacentDuplicateWords(title: string): string {
  return title.replace(/\b(\p{L}+)(\s+\1\b)+/giu, "$1");
}

/** Marketing hype and doubled punctuation — tone-only, never meaning. */
function collapsePunctuation(title: string): string {
  return title
    .replace(/[!¡]+/g, "")
    .replace(/([-–—:;,.])\1+/g, "$1")
    .replace(/\s{2,}/g, " ")
    .replace(/^[\s\-–—:,.]+|[\s\-–—:,.]+$/g, "")
    .trim();
}

export interface TitleDisplayOptions {
  isProvider: boolean;
  cityHint?:  string | null;
}

export function normalizeExperienceTitleForDisplay(
  title:   string,
  options: TitleDisplayOptions,
): string {
  if (!title) return title;

  // Always safe, regardless of source: whitespace and stutter-typo cleanup.
  let result = title.replace(/\s{2,}/g, " ").trim();
  result = collapseAdjacentDuplicateWords(result);

  // Provider-template-specific cleanup — local/reference copy is editorial
  // and left alone beyond the universal fixes above. cityHint is currently
  // unused (see the Part C removal note at the top of this file) but stays
  // part of the options shape — a future, provably-safe use may need it,
  // and removing it would be a breaking change to every call site for no
  // benefit.
  if (options.isProvider) {
    if (isShouting(result)) result = toTitleCase(result);
    result = collapsePunctuation(result);
  }

  return result.trim() || title;
}

// See titleDisplay.test.ts for real behavior tests, including the exact
// regression titles from Provider-Only Marketplace & Comparison V2
// Foundation, Part C (Palacio Real de Madrid, Pub Crawl de Madrid, Mexico
// City / Panama City / Quebec City / New York compound names).
